/**
 * VoiceCallContext — a live voice call on LiveKit (Docs/architecture/31).
 *
 * toggleVoice() asks the backend for a call into the active chat session
 * (POST /api/v1/voice/calls), joins the LiveKit room with the mic on, and
 * plays the Omni voice worker's audio. Captions come from LiveKit's
 * transcription streams; the agent's state from its `lk.agent.state`
 * attribute. Nothing here records the conversation: the server writes each
 * spoken turn into the chat session once, and Home refreshes on turn end.
 *
 * `available` is false when the server has no OMNI_LIVEKIT_URL; the orb and
 * mic button hide instead of offering a call that cannot start.
 *
 * `modes` are the voice modes the server offers (default first); the chosen
 * one is remembered per browser and sent with the next call. In a
 * speech-to-speech mode the worker records the spoken lines itself.
 *
 * Hands-free (RW-4.43): while on and no call is running, a wake-word engine
 * listens to the mic on this device (`modules/wake`, nothing leaves it) and
 * starts a call when it hears its phrase. The mic is held only while
 * listening, never during a call. A call the wake word started hangs up after
 * `HANDS_FREE_IDLE_MS` of quiet, and listening resumes.
 */
import * as React from "react";
import {
  ParticipantKind,
  Room,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type Participant,
  type RemoteTrack,
} from "livekit-client";
import { getVoiceStatus, startVoiceCall } from "@/api";
import { useApiClient } from "@/api/useApiClient";
import { ACTIVE_SESSION_KEY } from "@/hooks/useSessionMessages";
import { apiErrorMessage } from "@/lib/api-error";
import type { VoiceMode } from "@/lib/api-types";
import { DEFAULT_WAKE_ENGINE, WAKE_ENGINES } from "@/modules/wake/engine";
import { playWakeChime } from "@/modules/wake/chime";
import { startPreroll, type Preroll } from "@/modules/wake/preroll";
import { useControl } from "@/hooks/useControls";

export type WakeState = "off" | "starting" | "listening" | "error";

export type AgentState = "idle" | "listening" | "thinking" | "speaking";

export interface VoiceCallContextValue {
  /** The server has voice configured. */
  available: boolean;
  /** Voice modes the server offers, its default first. */
  modes: VoiceMode[];
  /** The mode the next call starts in. */
  mode: VoiceMode;
  setMode: (mode: VoiceMode) => void;
  /** Listen for the wake phrase while no call is running. */
  handsFree: boolean;
  /** An admin may withdraw the wake word (`voice.wake_word_enabled`): then no option, no listening. */
  wakeAllowed: boolean;
  setHandsFree: (on: boolean) => void;
  wakeState: WakeState;
  /** What to say to start a call hands-free. */
  wakePhrase: string;
  voiceActive: boolean;
  /**
   * A call is starting: the microphone is already recording (what is said now
   * is played to the agent as soon as it joins) and the orb shows it. True
   * from the click until the call is live or has failed.
   */
  connecting: boolean;
  agentState: AgentState;
  /** Mic level, [0, 1]. */
  micVolume: number;
  /** What the user is saying (live transcription). */
  interimText: string;
  /** What Omni is saying (live transcription). */
  assistantStreaming: string;
  micActive: boolean;
  error: string | null;
  /** Omni's speaking level, [0, 1]. */
  assistantLevel: number;
  /** Increments each time Omni finishes a spoken reply. */
  turnsCompleted: number;
  toggleVoice: () => void;
  toggleMic: () => void;
  cancelActiveCall: () => void;
}

const VoiceCallContext = React.createContext<VoiceCallContextValue | undefined>(undefined);

export function useVoiceCall() {
  const context = React.useContext(VoiceCallContext);
  if (!context) throw new Error("useVoiceCall must be used within a VoiceCallProvider");
  return context;
}

const TRANSCRIPTION_TOPIC = "lk.transcription";
const MODE_KEY = "omni.voice.mode";
const HANDS_FREE_KEY = "omni.voice.handsFree";
/** A hands-free call with nobody talking for this long hangs up. */
export const HANDS_FREE_IDLE_MS = 30_000;
/** Longest wait for the agent to subscribe before the pre-roll plays anyway. */
const PREROLL_WAIT_MS = 10_000;
/**
 * A second click this soon after the first is the same click (a double
 * click, a touch that registers twice); later it hangs up the starting call.
 */
const START_DEBOUNCE_MS = 700;
const AGENT_STATE_ATTR = "lk.agent.state";
const LEVEL_POLL_MS = 80;

function activeSessionId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_SESSION_KEY);
  } catch {
    return null;
  }
}

function remembered(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode: the choice lasts this page only.
  }
}

/** Open the microphone and start recording it for the call; null if it cannot (the call then asks for the mic itself). */
async function openPreroll(): Promise<Preroll | null> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
    });
    const recorder = await startPreroll(stream).catch((exc) => {
      stream.getTracks().forEach((t) => t.stop());
      throw exc;
    });
    recorder.capture();
    return recorder;
  } catch {
    return null;
  }
}

function asAgentState(value: string | undefined): AgentState {
  return value === "listening" || value === "thinking" || value === "speaking" ? value : "idle";
}

export function VoiceCallProvider({ children }: { children: React.ReactNode }) {
  const client = useApiClient();
  const [available, setAvailable] = React.useState(false);
  const [modes, setModes] = React.useState<VoiceMode[]>([]);
  const [mode, setModeState] = React.useState<VoiceMode>("pipeline");
  const [voiceActive, setVoiceActive] = React.useState(false);
  const [connecting, setConnecting] = React.useState(false);
  const [agentState, setAgentState] = React.useState<AgentState>("idle");
  const [micActive, setMicActive] = React.useState(true);
  const [interimText, setInterimText] = React.useState("");
  const [assistantStreaming, setAssistantStreaming] = React.useState("");
  const [micVolume, setMicVolume] = React.useState(0);
  const [assistantLevel, setAssistantLevel] = React.useState(0);
  const [turnsCompleted, setTurnsCompleted] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const roomRef = React.useRef<Room | null>(null);
  const audioEls = React.useRef<HTMLMediaElement[]>([]);
  const agentStateRef = React.useRef<AgentState>("idle");
  /** The running call was started by the wake word (it may hang up when idle). */
  const wokeRef = React.useRef(false);
  /** A wake handed its microphone to the call: what was said after the phrase plays first (RW-4.43c). */
  const prerollRef = React.useRef<Preroll | null>(null);
  const lastActivityRef = React.useRef(0);
  /** Which start is current; ending a call (or starting another) retires the one in flight. */
  const startIdRef = React.useRef(0);
  const startedAtRef = React.useRef(0);
  const connectingRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    getVoiceStatus({ client })
      .then(({ data }) => {
        if (cancelled || !data) return;
        setAvailable(data.enabled);
        setModes(data.modes);
        // A remembered mode the server no longer offers falls back to its default.
        const kept = remembered(MODE_KEY);
        const pick = data.modes.find((m) => m === kept) ?? data.modes[0];
        if (pick) setModeState(pick);
      })
      .catch(() => {
        if (!cancelled) setAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const teardown = React.useCallback(() => {
    startIdRef.current += 1;
    connectingRef.current = false;
    setConnecting(false);
    const room = roomRef.current;
    roomRef.current = null;
    prerollRef.current?.stop();
    prerollRef.current = null;
    audioEls.current.forEach((el) => el.remove());
    audioEls.current = [];
    if (room) void room.disconnect();
    // Cleared on every end, failed starts included: a manual call after a
    // wake that failed to connect must not inherit the idle hang-up.
    wokeRef.current = false;
    setVoiceActive(false);
    agentStateRef.current = "idle";
    setAgentState("idle");
    setInterimText("");
    setAssistantStreaming("");
    setMicVolume(0);
    setAssistantLevel(0);
  }, []);

  React.useEffect(() => teardown, [teardown]);

  // Levels drive the orb animation; LiveKit already computes them.
  React.useEffect(() => {
    if (!voiceActive) return;
    const id = window.setInterval(() => {
      const room = roomRef.current;
      if (!room) return;
      setMicVolume(room.localParticipant.audioLevel);
      const agent = [...room.remoteParticipants.values()].find((p) => p.kind === ParticipantKind.AGENT);
      setAssistantLevel(agent?.audioLevel ?? 0);
    }, LEVEL_POLL_MS);
    return () => window.clearInterval(id);
  }, [voiceActive]);

  const setMode = React.useCallback((next: VoiceMode) => {
    setModeState(next);
    remember(MODE_KEY, next);
  }, []);

  const [handsFree, setHandsFreeState] = React.useState(() => remembered(HANDS_FREE_KEY) === "1");
  const [wakeState, setWakeState] = React.useState<WakeState>("off");
  const wakeAllowed = useControl<boolean>("voice.wake_word_enabled", true);
  const setHandsFree = React.useCallback((on: boolean) => {
    setHandsFreeState(on);
    remember(HANDS_FREE_KEY, on ? "1" : "0");
  }, []);
  const wakeEngine = WAKE_ENGINES[DEFAULT_WAKE_ENGINE];


  /**
   * Start a call. The microphone opens and starts recording at once, in
   * parallel with the server round trip and the room connect, so the user
   * can start talking the moment they click (or say the wake phrase): the
   * recording is the call's first audio, played when the agent joins
   * (RW-4.43c). A start that is retired meanwhile (hang-up, another start)
   * cleans up after itself and shows nothing.
   */
  const startCall = React.useCallback(async () => {
    if (roomRef.current || connectingRef.current) return;
    setError(null);
    const sessionId = activeSessionId();
    if (!sessionId) {
      setError("Open a chat first — the call joins that conversation.");
      return;
    }
    const id = ++startIdRef.current;
    const retired = () => id !== startIdRef.current;
    connectingRef.current = true;
    startedAtRef.current = Date.now();
    setConnecting(true);
    // A wake already holds a recording; a click opens one now.
    const opening = prerollRef.current || !micActive ? null : openPreroll();
    // The recording, once the call owns it (teardown then stops it).
    let adopted = false;
    try {
      const { data } = await startVoiceCall({
        client,
        body: { session_id: sessionId, mode },
        throwOnError: true,
      });
      const recorded = opening ? await opening : null;
      if (retired()) {
        recorded?.stop();
        return;
      }
      if (recorded) {
        prerollRef.current = recorded;
        adopted = true;
      }
      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track.kind !== Track.Kind.Audio) return;
        const el = track.attach();
        el.style.display = "none";
        document.body.appendChild(el);
        audioEls.current.push(el);
      });
      room.on(RoomEvent.ParticipantAttributesChanged, (_changed: Record<string, string>, p: Participant) => {
        if (p.kind !== ParticipantKind.AGENT) return;
        const next = asAgentState(p.attributes[AGENT_STATE_ATTR]);
        if (agentStateRef.current === "speaking" && next !== "speaking") setTurnsCompleted((n) => n + 1);
        agentStateRef.current = next;
        setAgentState(next);
      });
      room.on(RoomEvent.Disconnected, () => teardown());
      room.registerTextStreamHandler(TRANSCRIPTION_TOPIC, (reader, participant) => {
        const fromUser = participant.identity === room.localParticipant.identity;
        const set = fromUser ? setInterimText : setAssistantStreaming;
        void (async () => {
          let text = "";
          for await (const chunk of reader) {
            text += chunk;
            set(text);
          }
        })();
      });

      await room.connect(data.url, data.token);
      if (retired()) return;
      const preroll = prerollRef.current;
      if (preroll) {
        // A wake: the microphone is the pre-roll track, which plays what was
        // said after the phrase and then catches up with the live mic. It
        // starts playing only once the agent has subscribed — the agent joins
        // a moment after the call starts, and audio sent before that is lost.
        const heard = new Promise<void>((resolve) => {
          const done = () => {
            window.clearTimeout(timer);
            room.off(RoomEvent.LocalTrackSubscribed, onSubscribed);
            resolve();
          };
          const onSubscribed = (pub: LocalTrackPublication) => {
            if (pub.track?.mediaStreamTrack === preroll.track) done();
          };
          room.on(RoomEvent.LocalTrackSubscribed, onSubscribed);
          const timer = window.setTimeout(done, PREROLL_WAIT_MS);
        });
        await room.localParticipant.publishTrack(preroll.track, { source: Track.Source.Microphone });
        void heard.then(() => preroll.release());
        if (!micActive) await room.localParticipant.setMicrophoneEnabled(false);
      } else {
        await room.localParticipant.setMicrophoneEnabled(micActive);
      }
      if (retired()) return;
      setVoiceActive(true);
      setConnecting(false);
      connectingRef.current = false;
    } catch (exc) {
      // Hanging up while still connecting makes connect() reject: that is
      // the user's own cancel, not an error to show.
      const cancelled = retired();
      if (!cancelled) teardown();
      // The recording opened for this start but never became the call's: stop it, or the
      // microphone stays on after "could not start the call".
      if (!adopted) (await opening)?.stop();
      if (!cancelled) setError(apiErrorMessage(exc));
    }
  }, [client, micActive, mode, teardown]);

  // Hands-free: listen while on, voice is available and no call is running.
  const startCallRef = React.useRef(startCall);
  startCallRef.current = startCall;
  React.useEffect(() => {
    if (!handsFree || !wakeAllowed || !available || voiceActive || connecting) {
      setWakeState("off");
      return;
    }
    let stopped = false;
    let stream: MediaStream | null = null;
    let preroll: Preroll | null = null;
    const engine = wakeEngine.create(() => {
      if (stopped) return;
      playWakeChime();
      wokeRef.current = true;
      if (preroll) {
        // The call owns the microphone from here; this effect's cleanup
        // must not stop it.
        preroll.capture();
        prerollRef.current = preroll;
        preroll = null;
        stream = null;
      }
      void startCallRef.current();
    });
    setWakeState("starting");
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
        });
        if (stopped) return;
        await engine.start(stream);
        const recorder = await startPreroll(stream);
        if (stopped) {
          recorder.stop();
          return;
        }
        preroll = recorder;
        setWakeState("listening");
      } catch (exc) {
        if (stopped) return;
        setWakeState("error");
        setError(`Hands-free could not start: ${apiErrorMessage(exc)}`);
      }
    })();
    return () => {
      stopped = true;
      engine.stop();
      preroll?.stop();
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [handsFree, wakeAllowed, available, voiceActive, connecting, wakeEngine]);

  // A call the wake word started hangs up after a stretch of quiet.
  React.useEffect(() => {
    if (agentState !== "listening" || interimText) lastActivityRef.current = Date.now();
  }, [agentState, interimText, assistantStreaming]);
  React.useEffect(() => {
    if (!voiceActive) return;
    lastActivityRef.current = Date.now();
    const id = window.setInterval(() => {
      if (wokeRef.current && Date.now() - lastActivityRef.current > HANDS_FREE_IDLE_MS) teardown();
    }, 1000);
    return () => window.clearInterval(id);
  }, [voiceActive, teardown]);

  const toggleVoice = React.useCallback(() => {
    if (connectingRef.current) {
      // The same click twice is not a hang-up; a later click is.
      if (Date.now() - startedAtRef.current > START_DEBOUNCE_MS) teardown();
    } else if (roomRef.current) teardown();
    else void startCall();
  }, [startCall, teardown]);

  const toggleMic = React.useCallback(() => {
    setMicActive((on) => {
      const next = !on;
      void roomRef.current?.localParticipant.setMicrophoneEnabled(next);
      return next;
    });
  }, []);

  const value: VoiceCallContextValue = {
    available,
    modes,
    mode,
    setMode,
    handsFree: handsFree && wakeAllowed,
    wakeAllowed,
    setHandsFree,
    wakeState,
    wakePhrase: wakeEngine.phrase,
    voiceActive,
    connecting,
    agentState,
    micVolume,
    interimText,
    assistantStreaming,
    micActive,
    error,
    assistantLevel,
    turnsCompleted,
    toggleVoice,
    toggleMic,
    cancelActiveCall: teardown,
  };

  return <VoiceCallContext.Provider value={value}>{children}</VoiceCallContext.Provider>;
}
